import {
  Box,
  Button,
  Center,
  HStack,
  Image,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useMutation, useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import translationButtonImage from "../../../../../../assets/images/translationButtonImage.png";

import { filterLinks, getPingMessage } from "../../../../../../utils";
import { disableOpenPhone } from "../../../../../../utils/api/dealership-setting.api";
import { markMessageAsRead } from "../../../../../../utils/api/header-notifications.api";
import { fetchUsersDP } from "../../../../../../utils/api/users.api";
import { useBreadcrumb } from "../../../../../../utils/context/BreadCrumsContext/BreadcrumbContext";
import { useUserInfo } from "../../../../../../utils/hooks/useUserInfo";
import Loader from "../../../../../common/Spinner";
import TooltipIfOverflow from "../../../../../common/TooltipIfOverflow";
import NoItem from "../NoItem";
import MessageBody from "./MessageBody";
import MessageHeader from "./MessageHeader";
import MessageStyles from "./Messages.module.scss";

type MessageThreadType = {
  clickedRecord: any;
  setClickedRecord: Dispatch<SetStateAction<any>>;
  selectedDate?: string;
  clickedRow: any;
  type: string;
  messageThreadProps?: any;
  ping?: boolean;
};

function MessageThread({
  setClickedRecord,
  messageThreadProps,
  clickedRow,
  ping = false,
  type,
}: MessageThreadType) {
  const {
    lineDevices,
    ref,
    sendMessage,
    thread,
    attachmentMutation,
    dateFormatter,
    divRefCurrent,
    device,
    setDevice,
    attachments,
    setAttachments,
    messageBody,
    setMessageBody,
    mutation,
    setMessageThread,
    setInitialLoad,
    setMentions,
    setTranslate,
    translateMessage,
    translatedArray,
    setTranslatedArray,
    loading,
  } = messageThreadProps;

  const isGuestMode = type === "guest-message";

  const { messages = [] } = thread || {};

  const user = useUserInfo("user");
  const { disableOnErrorRes } = useBreadcrumb();

  const { data: isOpenPhone } = useQuery({
    queryKey: ["openPhone", user?.dealership?.id],
    queryFn: disableOpenPhone,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !isGuestMode,
  });

  const [shouldShowIndicator, setShouldShowIndicator] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const messageMutation = useMutation(markMessageAsRead);

  useEffect(() => {
    // Cleanup previous observer if it exists
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("data-id");
          const readStatus = entry.target.getAttribute("data-read");
        }
      });
    });

    const elements = document.querySelectorAll(".list-item");
    elements.forEach((element) => observer.current?.observe(element));

    // Cleanup observer on unmount
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const maxCharLimit = 1600; // Set your max character limit
  const perMessageLimit = 160;
  const charCount = messageBody.length;
  const isLimitReached = charCount >= maxCharLimit;

  const options = [
    { label: "To English", value: "english" },
    { label: "To Spanish", value: "spanish" },
  ];

  useEffect(() => {
    return () => {
      const { current }: { current: any } = ref;
      current?.remove();
      ref.current = null;
      setMessageThread({});
      setInitialLoad(false);
      setMessageBody("");
      setTranslate({});
      setTranslatedArray([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, setInitialLoad, setMessageThread, setMessageBody]);

  function fetchUsers(query: any, callback: any) {
    // if (!query) return;
    try {
      fetchUsersDP({
        searchValue: query,
        dealership_id: user.dealership.id,
        status: "active",
      })
        // Transform the users to what react-mentions expects
        .then((res) => {
          return (
            res?.users
              ?.filter((userInst: any) => userInst.id !== user.id)
              ?.map((user: any) => ({
                display: user.full_name,
                id: user.id,
              })) || []
          );
        })
        .then(callback);
    } catch (error) {}
  }

  function shouldBtnDisabled() {
    if (isGuestMode) {
      return clickedRow?.dnc;
    } else {
      return (
        disableOnErrorRes ||
        clickedRow?.block ||
        !(messageBody && device) ||
        isLimitReached ||
        !clickedRow?.phone_number ||
        !isOpenPhone ||
        !isOpenPhone?.data?.within_hours
      );
    }
  }
  console.log(messages);
  return (
    <VStack
      w="100%"
      alignItems="flex-start"
      background="var(--grey-50)"
      padding="1rem 1.3rem"
      paddingTop="0"
      h={`calc(100vh - ${ping ? 100 : 260}px)`}
      id="message-body"
    >
      {(attachmentMutation.isLoading || mutation.isLoading) && <Loader />}
      {!isGuestMode && (
        <MessageHeader setClickedRecord={setClickedRecord}></MessageHeader>
      )}
      <VStack
        w="100%"
        alignItems="flex-start"
        padding="1rem"
        border="1px solid var(--grey-300)"
        position="relative"
        h="100%"
        borderRadius="0.5rem"
      >
        <VStack
          alignItems="flex-start"
          w="100%"
          h={isGuestMode ? "70%" : `calc(100vh - ${ping ? 400 : 580}px)`}
          overflowY="auto"
          marginBottom="auto"
          gap="0.96rem"
          padding="1.3rem"
          pb="92px"
          ref={ref as any}
          id="message-container"
          onScroll={({ target }: { target: any }) => {
            const isAtBottom =
              target.scrollHeight - target.clientHeight <= target.scrollTop + 1; // Adding a tolerance
            if (isAtBottom) {
              setShouldShowIndicator(false);
            } else {
              !shouldShowIndicator && setShouldShowIndicator(true);
            }
          }}
        >
          {loading && (
            <Center
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              w="100%"
              h="100%"
              bg="rgba(255, 255, 255, 0.7)" // Optional: semi-transparent background
              zIndex={1}
            >
              <Spinner size="md" color="blue.500" />
            </Center>
          )}

          {!!messages?.length ? (
            messages.map((item: any, index: number) => {
              const showDateSeparator =
                dateFormatter(messages[index]?.created_at) !==
                  dateFormatter(messages[index - 1]?.created_at) ||
                !messages.some(
                  (message: any) =>
                    dateFormatter(message.created_at) ===
                    dateFormatter(messages[0]?.created_at)
                );

              return (
                <>
                  {showDateSeparator && (
                    <HStack w="100%" gap="1.5rem">
                      <Box flex="1" h="1px" bg="var(--grey-300)" />
                      <Text
                        fontSize="0.71rem"
                        fontWeight="500"
                        color="var(--grey-700)"
                        whiteSpace="nowrap"
                      >
                        {moment(item.created_at).format("ddd MMM D, YYYY")}
                      </Text>
                      <Box flex="1" h="1px" bg="var(--grey-300)" />
                    </HStack>
                  )}
                  <VStack
                    w="50%"
                    gap="0.25"
                    maxW="22rem"
                    alignSelf={
                      item.message_direction === "inbound"
                        ? "flex-start"
                        : "flex-end"
                    }
                    ref={index === messages.length - 1 ? divRefCurrent : null}
                  >
                    <HStack w="100%" justifyContent="space-between">
                      <Text
                        fontSize="0.71rem"
                        color="var(--grey-700)"
                        fontWeight="500"
                      >
                        {item.is_scheduled ? (
                          <>
                            <Text textStyle="h6" color="var(--secondary-600)">
                              Scheduled At:
                            </Text>{" "}
                            {moment(item.scheduled_at).format(
                              "ddd MMM D, YYYY hh:mm a"
                            )}
                          </>
                        ) : (
                          moment(item.created_at).format(
                            "ddd MMM D, YYYY hh:mm a"
                          )
                        )}
                      </Text>
                      <Text
                        fontSize="0.83rem"
                        color="var(--grey-900)"
                        fontWeight="400"
                      >
                        <TooltipIfOverflow>
                          {item.message_source === "sarah_ai"
                            ? "Sarah.ai"
                            : item.message_direction === "inbound"
                            ? item?.contact_name
                            : item.user_name
                            ? item.user_name
                            : item.from}
                        </TooltipIfOverflow>
                      </Text>
                    </HStack>
                    <VStack
                      alignItems="flex-start"
                      padding="0.96rem 1.32rem 0.67rem 0.9rem"
                      borderRadius="0.5rem"
                      alignSelf={
                        item.message_direction === "inbound"
                          ? "baseline"
                          : "end"
                      }
                      width="100%"
                      style={
                        !(item.message_direction === "inbound")
                          ? {
                              background: "#fafafa",
                              boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
                            }
                          : { background: "var(--secondary-50)" }
                      }
                      className="messageHistory list-item"
                      data-id={item.id}
                      data-read={item.is_read}
                    >
                      <Text textStyle="h6" color="var(--secondary-600)">
                        {item.from}
                      </Text>{" "}
                      <Text
                        fontSize="0.83rem"
                        color="var(--grey-900)"
                        fontWeight="500"
                        wordBreak="break-word"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            getPingMessage(item.content)
                          ),
                        }}
                      />
                      <Box
                        mt="0.5rem"
                        display="flex"
                        flexWrap="wrap"
                        width="100%"
                      >
                        {filterLinks(item.media_urls || [])?.map(
                          (media: any) => {
                            return (
                              <a href={media} download about="_blank">
                                <img
                                  src={media}
                                  alt={media}
                                  width={100}
                                  height={100}
                                />
                              </a>
                            );
                          }
                        )}
                      </Box>

                      {!isGuestMode && item?.content && (
                        <VStack w="100%" alignItems="flex-start">
                          {!translatedArray?.find(
                            (items: any) => items?.id == item?.id
                          ) && !item?.translation?.language ? (
                            <HStack justifyContent="flex-end" w="100%">
                              <Button
                                variant="none"
                                onClick={() => {
                                  translateMessage({
                                    content: item.content,
                                    id: item.id,
                                  });
                                }}
                              >
                                <Image
                                  src={translationButtonImage}
                                  alt="translate"
                                  w="30px"
                                />
                              </Button>
                            </HStack>
                          ) : (
                            <>
                              <Text
                                fontSize="0.74rem"
                                color="var(--grey-900)"
                                fontWeight="700"
                                fontStyle="bold"
                              >
                                Translated Text:
                              </Text>
                              <Text
                                fontSize="0.73rem"
                                color="var(--grey-600)"
                                fontWeight="500"
                                fontStyle="italic"
                                maxW="100%"
                              >
                                {translatedArray?.find(
                                  (items: any) => items?.id == item?.id
                                )?.translated ||
                                  item?.translation?.translated_text}
                              </Text>{" "}
                            </>
                          )}
                        </VStack>
                      )}
                    </VStack>
                  </VStack>
                </>
              );
            })
          ) : !loading ? (
            <NoItem />
          ) : null}

          {shouldShowIndicator && (
            <span
              className={MessageStyles.ScrollIndicator}
              onClick={() => {
                const objDiv = document.getElementById("message-container");
                if (objDiv) objDiv.scrollTop = objDiv.scrollHeight;
              }}
            >
              <KeyboardArrowDownIcon style={{ marginTop: 6 }} />
            </span>
          )}
        </VStack>

        <MessageBody
          messageBody={messageBody}
          setMessageBody={setMessageBody}
          attachments={attachments}
          setAttachments={setAttachments}
          device={device}
          setDevice={setDevice}
          fetchUsers={fetchUsers}
          charCount={charCount}
          perMessageLimit={perMessageLimit}
          isLimitReached={isLimitReached}
          isGuestMode={isGuestMode}
          clickedRow={clickedRow}
          lineDevices={lineDevices}
          shouldBtnDisabled={shouldBtnDisabled}
          sendMessage={sendMessage}
          setMentions={setMentions}
        />
      </VStack>
    </VStack>
  );
}

export default MessageThread;
